import { BaseHit, Hit } from 'instantsearch.js';
import { Product } from 'shared/types/product';
import { getLocalizationInfo } from 'project.config';
import { offer, ProductAlgolia } from './algoliaProduct';
import useLocale from 'helpers/hooks/useLocale';
import { getLocalizedField, productUrl } from 'helpers/product/productUrl';

interface ProductHitVariant {
  id: string;
  sku: string;
  images: string[];
  prices: Record<string, { centAmount: number; fractionDigits: number }>;
  isOnStock: boolean;
}

export interface ProductHit extends BaseHit {
  productId: string;
  categories: Array<{ id: string }>;
  name: { [locale: string]: string };
  slug: { [locale: string]: string };
  variants: Array<ProductHitVariant>;
  standalonePrice?: price[];
  cartDiscount?: CartDiscount[];
  customFit?: string;
  images?: string[];
  swatches?: Swatch[];
  variantCode?: string;
  colour?: string;
  colourDisplay?: string;
  attributes?: {
    firstReceiptDate?: string;
    [key: string]: any; // Allow additional attributes
  };
}
export type Swatch = {
  prices?: price[];
  images: string[];
  sku: string;
  variantCode: string;
  colourHexCode: string;
  colour?: string;
};
type price = {
  priceEU: number;
  priceGB: number;
  mrrpPriceGB?: number;
  mrrpPriceEU?: number;
  wasPriceGB?: number;
  wasPriceEU?: number;
  variantSku: string;
};

type CartDiscount = {
  key: string;
  name: {
    'en-GB': string;
  };
  sku?: string;
};
export interface ProductName {
  [locale: string]: string;
}
export const convertProductNameToUrl = (input: string) => {
  const urlformat = `${input?.toLowerCase()?.replace(/\s+/g, '-')}`;
  return urlformat.endsWith('-') ? urlformat : `${urlformat}-`;
};

export const mapProduct = (
  hit: Hit<BaseHit>,
  locale: string,
  localeKey: string,
  fallbackLocale: string,
): ProductAlgolia => {
  const productHit = hit as Hit<ProductHit>;
  const name = getLocalizedField(productHit?.name, localeKey, fallbackLocale);
  const slug = getLocalizedField(productHit?.slug, localeKey, fallbackLocale);
  const description = getLocalizedField((productHit as any)?.description, localeKey, fallbackLocale);
  const { currency } = getLocalizationInfo(locale);
  let url = '';
  // if (typeof window !== 'undefined') {
  //   url = `${window.location.pathname}${productName ? convertProductNameToUrl(productName) : ''}${slug}.html`;
  // }

  return {
    objectId: productHit?.objectID,
    productId: productHit?.productId,
    productKey: productHit?.key,
    name: name,
    customFit: productHit?.customFit,
    slug: slug,
    standalonePrice: productHit?.standalonePrice,
    cartDiscount: productHit?.cartDiscount,
    categories: productHit?.categories,
    variants: productHit?.variants?.map((variant) => ({
      ...variant,
      price: variant?.prices?.[currency]
        ? { ...variant?.prices[currency], currencyCode: currency }
        : { centAmount: 0, fractionDigits: 2, currencyCode: currency }, // Fallback price
    })),
    _url: productUrl(productHit, fallbackLocale, localeKey),
    description: description,
    offer: productHit?.offer as offer[],
    colourImages: productHit?.images,
    swatches: productHit?.swatches,
    variantCode: productHit?.variantCode,
    colour: productHit?.colour,
    colourDisplay: productHit?.colourDisplay,
    firstReceiptDate: productHit?.attributes?.firstReceiptDate,
  };
};
